import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { listSafetyAcknowledgements } from '../../services/safetyLoginService';

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const PRESETS = [
  { label: 'Today', from: todayStr(), to: todayStr() },
  { label: 'Last 7 days', from: daysAgoStr(6), to: todayStr() },
  { label: 'Last 30 days', from: daysAgoStr(29), to: todayStr() },
  { label: 'Last 90 days', from: daysAgoStr(89), to: todayStr() },
];

function SummaryCard({ label, value, accent }) {
  return (
    <div className="panel" style={{ padding: '18px 20px' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color: accent ? 'var(--accent)' : 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

export default function SafetyAcknowledgementsReport({ embedded = false }) {
  const [filters, setFilters] = useState({
    from: daysAgoStr(29),
    to: todayStr(),
    driverId: '',
    status: '',
    limit: '1000',
  });
  const [submitted, setSubmitted] = useState({ ...filters });
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasRun, setHasRun] = useState(false);

  const acknowledged = events.filter((e) => e.acknowledgedAt).length;
  const pending = events.length - acknowledged;
  const ackRate = events.length > 0 ? Math.round((acknowledged / events.length) * 100) : null;

  const runReport = useCallback(async (params) => {
    setLoading(true);
    setError('');
    try {
      const query = { limit: params.limit || 1000 };
      if (params.from) query.from = params.from;
      if (params.to) query.to = params.to;
      if (params.driverId?.trim()) query.driverId = params.driverId.trim();
      if (params.status) query.status = params.status;
      const res = await listSafetyAcknowledgements(query);
      setEvents(res.data.events || []);
      setTotal(res.data.total ?? res.data.events?.length ?? 0);
      setHasRun(true);
    } catch {
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Run on mount with default filters
  useEffect(() => { runReport(submitted); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted({ ...filters });
    runReport(filters);
  };

  const applyPreset = (p) => {
    const next = { ...filters, from: p.from, to: p.to };
    setFilters(next);
    setSubmitted(next);
    runReport(next);
  };

  const exportCsv = () => {
    const headers = ['Driver ID', 'Driver Name', 'Cab #', 'Language', 'Login At', 'Acknowledged At', 'Delay (min)', 'Status'];
    const rows = events.map((ev) => {
      const loginTs = ev.loginAt ? new Date(ev.loginAt).getTime() : null;
      const ackTs = ev.acknowledgedAt ? new Date(ev.acknowledgedAt).getTime() : null;
      const delay = loginTs && ackTs ? Math.round((ackTs - loginTs) / 60000) : '';
      return [
        ev.driverId,
        ev.driverName,
        ev.cabNumber,
        ev.messageLanguage,
        ev.loginAt ? new Date(ev.loginAt).toISOString() : '',
        ev.acknowledgedAt ? new Date(ev.acknowledgedAt).toISOString() : '',
        delay,
        ev.acknowledgedAt ? 'Acknowledged' : 'Pending',
      ];
    });
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safety-ack-${submitted.from}-to-${submitted.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const content = (
    <>
      {/* ── Filters ───────────────────────────────────── */}
      <div className="surface" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSubmit}>
          <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: '0.8rem' }}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="form-grid" style={{ maxWidth: 800 }}>
            <div>
              <label htmlFor="sar-from">From</label>
              <input
                id="sar-from"
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="sar-to">To</label>
              <input
                id="sar-to"
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="sar-driver">
                Driver ID <span style={{ color: 'var(--text-secondary)' }}>(optional)</span>
              </label>
              <input
                id="sar-driver"
                type="text"
                value={filters.driverId}
                onChange={(e) => setFilters((p) => ({ ...p, driverId: e.target.value }))}
                placeholder="Filter by driver"
              />
            </div>
            <div>
              <label htmlFor="sar-status">Status</label>
              <select
                id="sar-status"
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="">All</option>
                <option value="acknowledged">Acknowledged only</option>
                <option value="pending">Pending only</option>
              </select>
            </div>
            <div>
              <label htmlFor="sar-limit">Limit</label>
              <select
                id="sar-limit"
                value={filters.limit}
                onChange={(e) => setFilters((p) => ({ ...p, limit: e.target.value }))}
              >
                <option value="500">500</option>
                <option value="1000">1 000</option>
                <option value="2000">2 000</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading…' : 'Run report'}
              </button>
              {hasRun && events.length > 0 && (
                <button type="button" className="btn btn-ghost" onClick={exportCsv}>
                  Export CSV
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {error && (
        <div className="feedback error" style={{ marginBottom: 16 }}>
          {error}{' '}
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: '0.8rem', marginLeft: 8 }}
            onClick={() => runReport(submitted)}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Summary cards ─────────────────────────────── */}
      {hasRun && !loading && events.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <SummaryCard label="Total logins" value={total.toLocaleString()} />
          <SummaryCard label="Acknowledged" value={acknowledged.toLocaleString()} accent />
          <SummaryCard label="Pending" value={pending.toLocaleString()} />
          <SummaryCard
            label="Ack rate"
            value={ackRate !== null ? `${ackRate}%` : '—'}
            accent={ackRate !== null && ackRate >= 95}
          />
        </div>
      )}

      {/* ── Table ─────────────────────────────────────── */}
      {hasRun && (
        <div className="surface">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <h3 style={{ margin: 0 }}>Login events</h3>
            {!loading && (
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {total.toLocaleString()} record{total !== 1 ? 's' : ''}
                {events.length < total ? ` (showing first ${events.length})` : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="skeleton" style={{ height: 200, borderRadius: 12 }} />
          ) : events.length === 0 ? (
            <div className="empty-state">No records found for the selected filters.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver ID</th>
                    <th>Name</th>
                    <th>Cab #</th>
                    <th>Lang</th>
                    <th>Login at</th>
                    <th>Acknowledged at</th>
                    <th>Delay</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => {
                    const loginTs = ev.loginAt ? new Date(ev.loginAt).getTime() : null;
                    const ackTs = ev.acknowledgedAt ? new Date(ev.acknowledgedAt).getTime() : null;
                    const delayMin = loginTs && ackTs ? Math.round((ackTs - loginTs) / 60000) : null;
                    return (
                      <tr key={ev._id}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {ev.driverId}
                          </span>
                        </td>
                        <td>{ev.driverName || '—'}</td>
                        <td>{ev.cabNumber || '—'}</td>
                        <td style={{ textTransform: 'uppercase', fontSize: '0.8rem' }}>
                          {ev.messageLanguage || 'EN'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {fmtDateTime(ev.loginAt)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {fmtDateTime(ev.acknowledgedAt)}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {delayMin !== null ? `${delayMin} min` : '—'}
                        </td>
                        <td>
                          <span className={`badge ${ev.acknowledgedAt ? 'badge-success' : 'badge-warning'}`}>
                            {ev.acknowledgedAt ? 'Acknowledged' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <AppLayout
      title="Safety Acknowledgements"
      subtitle="Audit every login safety notice delivery and driver acknowledgement."
      actions={
        <Link to="/reports/driver-safety?tab=login-message" className="btn btn-ghost">
          Configure message
        </Link>
      }
    >
      {content}
    </AppLayout>
  );
}
